/**
 * SoundBoard — modal grids for the two legacy soundboard panels.
 * Hover over a clip to preview it locally (does not broadcast).
 * Click sends `sound_mess` to broadcast to the room.
 */
import { Modal } from './Modal.js';
import { play as playSound } from '../lib/soundboard.js';
import { usePrefs } from '../hooks/usePrefs.js';

interface Clip { label: string; key: string; bonus?: boolean; big?: boolean; }

// ---- Arnold board (faithful labels from the SWF sprite) -----------------
// Original layout (from DefineSprite_344 frame 1) was a 6-column grid:
// col1: Yes, Yea, No Deal, No, Negative, Hey, Good Morning, Good bye, ...
// col2: Turbo Time, Hastalavista, Quiet, Terminated, Erased, Get Out!, ...
// col3: *Cookies* (head), Eat my Cookies?!, Put the cookie down, ...
// col4: Hey I'm a police officer, Nice to meet you, ...
// col5: Gonna ask questions, Few more questions, Whos your daddy, ...
// col6 (bonus, red): Can't kill people. Why?, We stopped judgement,
//      Got to be shitting me.
// + bottom big red WALRUS button (calls server `walrus` cmd, not a clip)
const ARNOLD: Clip[] = [
    // col 1
    { label: 'Yes',          key: 'arnold/yes' },
    { label: 'Yea',          key: 'arnold/yea' },
    { label: 'No Deal',      key: 'arnold/no_deal' },
    { label: 'No',           key: 'arnold/no' },
    { label: 'Negative',     key: 'arnold/negativ' },
    { label: 'Hey',          key: 'arnold/hey' },
    { label: 'Good Morning', key: 'arnold/good_morning' },
    { label: 'Good bye',     key: 'arnold/good_bye' },
    { label: 'Who are you?', key: 'arnold/who_are_you' },
    { label: 'How are you',  key: 'arnold/how_are_you' },
    { label: 'Why',          key: 'arnold/why' },
    { label: 'Why Not',      key: 'arnold/why_not' },
    { label: 'No Problem',   key: 'arnold/no_problemo' },
    { label: 'No shit',      key: 'arnold/no_shit' },
    // col 2
    { label: 'Turbo Time',   key: 'arnold/turbo_time' },
    { label: 'Hastalavista', key: 'arnold/hasta' },
    { label: 'Quiet',        key: 'arnold/quiet' },
    { label: 'Terminated',   key: 'arnold/terminated' },
    { label: 'Erased',       key: 'arnold/erased' },
    { label: 'Get Out!',     key: 'arnold/get_out' },
    { label: "Don't do that",key: 'arnold/dont_do_that' },
    { label: 'You Idiot!',   key: 'arnold/you_idiot' },
    { label: 'hahahaha',     key: 'arnold/hahaha' },
    { label: 'Sorry',        key: 'arnold/sorry' },
    { label: 'I hear you',   key: 'arnold/i_hear_you' },
    { label: 'I lied',       key: 'arnold/lied' },
    { label: 'Postponed',    key: 'arnold/postponed' },
    // col 3 — cookies + body
    { label: 'Cookies',          key: 'arnold/cookies' },
    { label: 'Eat my Cookies?!', key: 'arnold/eat_my_cookies' },
    { label: 'Put the cookie down', key: 'arnold/put_cookie_down' },
    { label: 'Stop Whining',     key: 'arnold/stop_whining' },
    { label: 'Stop Shouting',    key: 'arnold/stop_shouting' },
    { label: 'Stop it',          key: 'arnold/stop_it' },
    { label: 'Talk to the hand', key: 'arnold/talk_to_the_hand' },
    { label: 'You lack discipline', key: 'arnold/you_lack_discipline' },
    { label: "Don't do that",    key: 'arnold/dont_do_dat' },
    // col 4 — cop persona
    { label: "I'm a police officer", key: 'arnold/im_a_police' },
    { label: 'Nice to meet you',     key: 'arnold/nice_to_meet_you' },
    { label: 'Det. John Kimble',     key: 'arnold/im_detective' },
    { label: "I'm a cop you idiot!", key: 'arnold/im_a_cop' },
    { label: 'This is a arrest',     key: 'arnold/an_arrest' },
    { label: 'Programmed to follow', key: 'arnold/programmed' },
    { label: 'My Mission is to…',    key: 'arnold/mission' },
    // col 5 — questioning
    { label: 'Gonna ask questions',  key: 'arnold/gonna_ask_questions' },
    { label: 'Few more questions',   key: 'arnold/few_more_questions' },
    { label: 'Whos your daddy',      key: 'arnold/who_is_your_daddy' },
    { label: 'Get your mother',      key: 'arnold/get_your_mother' },
    { label: 'Talk to mother',       key: 'arnold/talk_to_your_mother' },
    { label: 'Will not kill anyone', key: 'arnold/will_not_kill' },
    { label: 'What do you want?',    key: 'arnold/what_do_you_want' },
    { label: "Can't answer",         key: 'arnold/cant_answer' },
    { label: "Can't kill",           key: 'arnold/cant_kill' },
];
const ARNOLD_BONUS: Clip[] = [
    // The 3 red bonus clips on the right of the legacy panel; using the
    // closest on-disk matches.
    { label: "Can't kill people. Why?", key: 'arnold/cant_kill',         bonus: true },
    { label: 'Stop judgement',         key: 'arnold/stop_shouting',      bonus: true },
    { label: "Shitting me",            key: 'arnold/you_idiot',          bonus: true },
];

// ---- Random/Tass board (faithful labels from DefineSprite_250) ----------
const RANDOM: Clip[] = [
    { label: 'Snake "!"',                 key: 'tass/TassNo' },     // closest
    { label: 'No',                        key: 'tass/TassNo' },
    { label: 'Why?',                      key: 'tass/TassWhy' },
    { label: "I don't know!?!",           key: 'tass/TassIDontKnow' },
    { label: 'Not so much',               key: 'tass/TassNotSoMuch' },
    { label: 'Nice',                      key: 'tass/TassNice' },
    { label: 'This is sparta!',           key: 'tass/TassSparta' },
    { label: "We ain't playin'!",         key: 'tass/weaintplayin_short' },
    { label: 'Please play me in connect 4', key: 'tass/play_connect4' },
    { label: 'Go sit in the corner',      key: 'tass/TassTimeOut' },
    { label: 'I love you so much!',       key: 'tass/TassILoveYou' },
    { label: "Screw you guys, I'm going home", key: 'tass/TassScrewYouGuys' },
    { label: 'She lookie like a man',     key: 'tass/TassSheLookLikeAMan' },
];

interface Props {
    board: 'arnold' | 'random';
    room: string;
    onClose: () => void;
    onSend: (frame: object) => void;
}

export function SoundBoard({ board, room, onClose, onSend }: Props) {
    const [prefs] = usePrefs();
    const clips = board === 'arnold' ? ARNOLD : RANDOM;
    const bonus = board === 'arnold' ? ARNOLD_BONUS : [];
    const title = board === 'arnold' ? 'Evil Sound Board' : 'Random Sound Board';

    const fire = (c: Clip) => {
        onSend({ cmd: 'sound_mess', room, p: c.key });
    };
    const preview = (c: Clip) => {
        // Local preview only — doesn't broadcast. Honours Play Sounds pref.
        if (prefs.playSounds) playSound(c.key);
    };

    return (
        <Modal title={title} onClose={onClose}
               className={`pc-modal-sb pc-modal-sb-${board}`}>
            <div className={`sb-grid ${board === 'arnold' ? 'sb-grid-arnold' : 'sb-grid-random'}`}>
                {clips.map((c, i) => (
                    <button key={i} className="sb-clip"
                            onClick={() => fire(c)}
                            onContextMenu={(e) => { e.preventDefault(); preview(c); }}
                            title={`${c.key} — click to broadcast, right-click to preview locally`}>
                        {c.label}
                    </button>
                ))}
            </div>
            {bonus.length > 0 && (
                <div className="sb-bonus">
                    {bonus.map((c, i) => (
                        <button key={i} className="sb-clip sb-clip-bonus"
                                onClick={() => fire(c)}
                                onContextMenu={(e) => { e.preventDefault(); preview(c); }}
                                title={`${c.key} — click to broadcast, right-click to preview`}>
                            {c.label}
                        </button>
                    ))}
                </div>
            )}
            {board === 'arnold' && (
                <div className="sb-walrus-row">
                    <button className="sb-walrus" onClick={() =>
                        onSend({ cmd: 'walrus', room })}>WALRUS</button>
                </div>
            )}
        </Modal>
    );
}
